import { postRaw, ApiProgressEvent, putRaw } from 'services/api';
import {
  FileTypeDetails,
  ImageAttrAction,
  ImageAttrBatch,
  ImageAttrState,
  ImageAttrs,
  NewImage,
  Orientation,
  ProcessImageError,
  PromiseStatus,
  RejectImgData,
  ResolveImgData,
  SetAbortControllerHandler,
  UpdateAssetImageRequestData,
  UploadProgressHandler,
  fileTypes,
} from '../types';
import { cleanFileName } from './strings';

export const dataURLtoBlob = (dataurl: string) => {
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/) || [];
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const dataURLtoFile = (dataURL: string, mimeType: string, fileName: string) => {
  const epoch = new Date().getTime();
  const byteString = atob(dataURL.split(',')[1]);
  const arrBuf = new ArrayBuffer(byteString.length);
  const u8arr = new Uint8Array(arrBuf);
  for (let i = 0; i < byteString.length; i++) {
    u8arr[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([arrBuf], { type: mimeType });
  return new File([blob], fileName, { lastModified: epoch, type: mimeType });
};

export const fileToBase64String = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64DataUri = (e.target?.result || '').toString();
      const base64String = base64DataUri.replace(/^data:(.*,)?/, '');
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getBlob = (dataUrl: string) => {
  try {
    const blob = dataURLtoBlob(dataUrl);
    return {
      status: 'SUCCESS',
      data: blob,
    };
  } catch (e) {
    return {
      status: 'FAILURE',
      data: e,
    };
  }
};

export const getFileSize = (dataUrl: string) => {
  const base64str = dataUrl.split('base64,')[1];
  const decoded = atob(base64str);
  const size = decoded.length;

  return size;
};

// STEP 2: gather additional information about the image and attempt to read it as an image
// Beyond the initial file tests for type & size in STEP 1, this further validates the file as an
// image as well as performing type conversion (bmp, tiff, webp => png) and scaling as necessary
export const processImage = (file: File, maxDimension = 0, destinationFolderId: number) =>
  new Promise((resolve: ResolveImgData, reject: RejectImgData) => {
    const image = new Image(); // used to load image into the canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    // output: image/png (all), image/jpeg (all), or image/webp (Chrome, Edge, FF, Opera)
    const selectedFileType = Object.values(fileTypes).find(
      (fileType) => fileType.mimeType === file.type,
    ) as FileTypeDetails; // invalid file types have already been filtered out
    const newFileType = fileTypes[selectedFileType.uploadKey];
    const newMimeType = newFileType.mimeType;
    const newFileExt = newFileType.ext;
    const newFileQuality = newMimeType === 'image/jpeg' ? 0.9 : 1;
    const newFileName = cleanFileName(file.name, newFileExt, `${newFileExt}_file`);
    const imgData = {
      file: {
        file: new File([file], newFileName, { lastModified: file.lastModified, type: file.type }),
        mimeType: newMimeType,
        name: newFileName,
        size: file.size,
      },
      selected: {
        height: 0,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        width: 0,
        destinationFolderId,
      },
      processStatus: PromiseStatus.Undefined,
      isSelected: false,
      uploadPct: 0,
      uploadStatus: PromiseStatus.Undefined,
    } as NewImage;

    image.onload = () => {
      const shouldResize = maxDimension && Math.max(image.height, image.width) > maxDimension;
      imgData.selected.height = image.height;
      imgData.selected.width = image.width;
      imgData.file.height = image.height;
      imgData.file.width = image.width;

      try {
        if (
          (shouldResize || newMimeType !== imgData.selected.mimeType) &&
          !newFileType.canAnimate
        ) {
          // resize the image
          const aspectRatio = image.width / image.height;
          const orientation = aspectRatio <= 1 ? Orientation.Portrait : Orientation.Landscape;
          const resizeHeight =
            orientation === Orientation.Portrait
              ? maxDimension
              : Math.round(maxDimension / aspectRatio);
          const resizeWidth =
            orientation === Orientation.Landscape
              ? maxDimension
              : Math.round(maxDimension * aspectRatio);
          const newHeight = shouldResize ? resizeHeight : image.height;
          const newWidth = shouldResize ? resizeWidth : image.width;

          canvas.height = newHeight;
          canvas.width = newWidth;
          context.drawImage(image, 0, 0, newWidth, newHeight); // 5-argument syntax

          // update the base with the converted or resized image
          const newDataUrl = canvas.toDataURL(newMimeType, newFileQuality);
          imgData.file.file = dataURLtoFile(newDataUrl, newMimeType, newFileName);
          imgData.file.size = imgData.file.file.size;
          imgData.file.height = newHeight;
          imgData.file.width = newWidth;
        }
        imgData.upload = {
          flipH: false,
          flipV: false,
          height: imgData.file.height,
          rotation: 0,
          width: imgData.file.width,
        };
        resolve(imgData);
      } catch (error) {
        // resize or type conversion failed
        if (newMimeType === imgData.selected.mimeType) {
          // resize failed (no type conversion was necessary): not a big deal, just resize on the server
          imgData.processError = ProcessImageError.Optimize;
          imgData.upload = {
            flipH: false,
            flipV: false,
            height: imgData.file.height,
            rotation: 0,
            width: imgData.file.width,
          };
          resolve(imgData);
        } else {
          // type conversion failed, so reject upload
          const errorMsg = ProcessImageError.TypeConversion;
          imgData.processError = errorMsg;

          reject({ msg: errorMsg, error, imgData });
        }
      }
    };

    image.onerror = (error) => {
      const errorMsg = ProcessImageError.ReadImage;
      imgData.processError = errorMsg;

      reject({ msg: errorMsg, error, imgData });
    };

    image.src = URL.createObjectURL(file);
  });

export const downloadFile = (url: string, fileName: string) => {
  fetch(url, { method: 'get', mode: 'cors' })
    .then((res) => res.blob())
    .then((blob) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64DataUri = e.target?.result as string;
        const virtualLink = document.createElement('a');
        virtualLink.setAttribute('download', fileName);
        virtualLink.setAttribute('href', base64DataUri);
        virtualLink.setAttribute('target', '_blank');
        virtualLink.click();
      };
      reader.readAsDataURL(blob);
    });
};

export const uploadFile = (
  image: NewImage,
  updateProgress: UploadProgressHandler,
  setAbortController: SetAbortControllerHandler,
) =>
  new Promise((resolve, reject) => {
    // axios & XMLHttpRequest give intermediate progress updates (fetch does not)
    const controller = new AbortController();

    setAbortController(image.id, controller);

    fileToBase64String(image.file.file)
      .then((base64String) => {
        const assetData = {
          assetName: image.file.name,
          height: image.file.height,
          source: base64String,
          width: image.file.width,
          destinationFolderId: image.selected.destinationFolderId,
          altText: image.file.altText,
        };
        return postRaw('/i/assetManager/images', assetData, {
          onUploadProgress: (event: ApiProgressEvent) => {
            if (event.total !== undefined) {
              updateProgress(image.id, Math.round((event.loaded / event.total) * 100));
            }
          },
          signal: controller.signal,
        });
      })
      .then(({ data: asset }) => {
        // the successful upload is useful here in the case where we have a single upload (detail view)
        // in the list view the return is not needed because the list will be refreshed
        updateProgress(image.id, 100, PromiseStatus.Fulfilled); // completed successfully
        resolve({ id: image.id, asset });
      })
      .catch((err) => {
        updateProgress(
          image.id,
          0,
          err.code === 'ERR_CANCELED' ? PromiseStatus.Aborted : PromiseStatus.Rejected,
        ); // upload failed

        reject({ id: image.id, err });
      });
  });

export const updateAssetData = (id: number, data: UpdateAssetImageRequestData) => {
  return putRaw(`/i/assetManager/images/${id}`, data);
};

export const imageAttrStateDefault = {
  [ImageAttrs.ALT_DESC]: '',
  [ImageAttrs.HEIGHT]: '',
  [ImageAttrs.SOURCE]: '',
  [ImageAttrs.STYLES]: '',
  [ImageAttrs.WIDTH]: '',
};

export const imageAttrReducer = (state: ImageAttrState, action: ImageAttrAction) => {
  switch (action.type) {
    case ImageAttrBatch.SET_NEW:
      return { ...imageAttrStateDefault, ...action.value };
    case ImageAttrBatch.UPDATE_BATCH:
      return { ...state, ...action.value };
    case ImageAttrs.ALT_DESC:
      return { ...state, [ImageAttrs.ALT_DESC]: action.value };
    case ImageAttrs.HEIGHT:
      return { ...state, [ImageAttrs.HEIGHT]: action.value };
    case ImageAttrs.SOURCE:
      return { ...state, [ImageAttrs.SOURCE]: action.value };
    case ImageAttrs.STYLES:
      return { ...state, [ImageAttrs.STYLES]: action.value };
    case ImageAttrs.WIDTH:
      return { ...state, [ImageAttrs.WIDTH]: action.value };
    default:
      return { ...state };
  }
};

export const arrayPartition = <ArrayValueType>(
  array: ArrayValueType[],
  partitioner: (arrayMember: ArrayValueType) => boolean,
) => {
  const arrayAccumulator: ArrayValueType[][] = [[], []];
  return array.reduce((accumulator, element) => {
    accumulator[partitioner(element) ? 0 : 1].push(element);
    return accumulator;
  }, arrayAccumulator);
};

export const canRemovePendingAsset = (newFile: NewImage) => {
  return (
    // failed initial inspection/processing
    newFile.processStatus === PromiseStatus.Rejected ||
    // upload was aborted or failed or not yet started
    [PromiseStatus.Aborted, PromiseStatus.Rejected, PromiseStatus.Undefined].includes(
      newFile.uploadStatus,
    )
  );
};

// asset is valid and no upload has been attempted yet
export const isValidPendingAsset = (pendingAsset: NewImage) => {
  return (
    pendingAsset.processStatus === PromiseStatus.Fulfilled &&
    pendingAsset.uploadStatus === PromiseStatus.Undefined
  );
};

// asset is valid and no upload has been attempted yet
export const isValidEditableAsset = (pendingAsset: NewImage) => {
  return (
    pendingAsset.processStatus === PromiseStatus.Fulfilled &&
    pendingAsset.uploadStatus === PromiseStatus.Fulfilled
  );
};

// takeover UPLOAD mode: assets have been added to the queue that have not been uploaded
// - upload has not been attempted/triggered
// - upload is not in progress
export const hasNewUploads = (pendingAssets: NewImage[]) => {
  return pendingAssets.some((asset) => {
    return (
      asset.processStatus === PromiseStatus.Fulfilled &&
      (asset.uploadStatus === PromiseStatus.Undefined ||
        asset.uploadStatus === PromiseStatus.Pending)
    );
  });
};

// returns an array of only the selected assets that have been modified
export const getEditChanges = (pendingAssets: NewImage[]) => {
  return pendingAssets.filter((pa) => {
    return (
      (pa.asset?.assetName || '') !== (pa.file.name || '') ||
      (pa.asset?.altText || '') !== (pa.file.altText || '')
    );
  });
};

// takeover EDIT mode: have any of the editable assets been changed
export const hasEditChanges = (pendingAssets: NewImage[]) => {
  return !!getEditChanges(pendingAssets).length;
};

export const getInvalidImgObj = (file: File, err: string) => {
  return {
    file: {
      file: new File([], file.name, { lastModified: file.lastModified, type: file.type }),
      mimeType: file.type,
      name: file.name,
      size: file.size,
    },
    processStatus: PromiseStatus.Rejected,
    selected: {
      height: 0,
      name: file.name,
      size: file.size,
      mimeType: file.type,
      width: 0,
    },
    uploadPct: 0,
    validationError: err,
  } as NewImage;
};

export const getFileTypeFromMimeType = (mimeType: string) => {
  return (
    Object.values(fileTypes).find((fileType) => fileType.mimeType === mimeType) ||
    ({ ext: '' } as FileTypeDetails)
  ).ext.toLocaleUpperCase();
};
